import { useEffect } from 'react';

/**
 * Публикует в CSS-переменную расстояние от нижнего края экрана до верха закреплённого элемента.
 *
 * Отличается от {@link useHeightVar} тем, что считает не сам элемент, а <b>всё занятое им снизу
 * место</b> — вместе с зазором, на который он приподнят над краем. Для полосы разговора это
 * принципиально: она стоит над нижней навигацией и над безопасной зоной, и страница, оставившая
 * место по одной лишь высоте, получает полосу поверх своего нижнего края. В чате туда приходится
 * поле ввода сообщения, и перекрытыми оказываются кнопка отправки и последняя строка текста.
 *
 * Пересчитывается не только на изменение самого элемента, но и на изменение видимой области:
 * открытая клавиатура на Android ужимает webview, и элемент, не поменявший ни высоты, ни отступа,
 * оказывается совсем в другом месте экрана.
 */
export const useBottomSpaceVar = (name: string, node: HTMLElement | null) => {
  useEffect(() => {
    const root = document.documentElement;
    if (!node) {
      root.style.removeProperty(name);
      return undefined;
    }

    const publish = () => {
      const rect = node.getBoundingClientRect();
      // Ориентир — видимая область, а не окно: при открытой клавиатуре они расходятся, и место
      // надо оставлять по той части экрана, которую человек действительно видит.
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      root.style.setProperty(name, `${Math.max(0, Math.round(viewportHeight - rect.top))}px`);
    };

    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(node);
    window.addEventListener('resize', publish);
    window.visualViewport?.addEventListener('resize', publish);
    window.visualViewport?.addEventListener('scroll', publish);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      window.visualViewport?.removeEventListener('resize', publish);
      window.visualViewport?.removeEventListener('scroll', publish);
      root.style.removeProperty(name);
    };
  }, [name, node]);
};
